import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  fetchCanonicalLineageProjection,
  type CanonicalLineageArtifactVersionRoot,
  type CanonicalLineageQueryFilters,
  type CanonicalLineageQueryRequest,
} from "../../api/canonicalLineage";
import { normalizeDagData } from "./model";
import { ALL_MODEL_CLASSIFICATIONS, parseDagSearch, type DagFilterState } from "./search";
import type { DagViewerGraph } from "./types";
import type { DagDirection, DagSummaryGroup, DagViewMode } from "./viewProjections";
import {
  canonicalProjectionToDag,
  mergeCanonicalLineagePages,
  parseCanonicalLineageRefKey,
  type MergedCanonicalLineagePages,
} from "./canonicalAdapter";

export const CANONICAL_LINEAGE_MAX_RETAINED_PAGES = 8;
export const CANONICAL_LINEAGE_MAX_RETAINED_RECORDS = 2_500;

interface CanonicalLineagePageReplay {
  cursor: string;
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
}

export interface CanonicalLineageWorkspaceDescriptor {
  projectId: string;
  mode: DagViewMode;
  direction: DagDirection;
  depth: number | null;
  selectedId: string | null;
  compareA: string | null;
  compareB: string | null;
  summaryGroup: DagSummaryGroup;
  searchQuery: string;
  filters: DagFilterState;
  asOf: string | null;
  recordedAsOf: string | null;
  snapshotToken: string | null;
}

export interface CanonicalLineageProjectionState {
  graph: DagViewerGraph | null;
  projection: MergedCanonicalLineagePages | null;
  isPending: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  retentionLimitReached: boolean;
  error: Error | null;
  fetchNextPage: () => Promise<unknown>;
}

export class UnsupportedCanonicalLineageSearchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnsupportedCanonicalLineageSearchError";
  }
}

export class UnsupportedCanonicalLineageRootError extends Error {
  constructor(mode: "focus" | "impact" | "compare" | "time_compare") {
    super(`${mode} requires an ArtifactVersion. Select a version node; other canonical record kinds cannot seed this projection yet.`);
    this.name = "UnsupportedCanonicalLineageRootError";
  }
}

function artifactVersionRoot(value: string | null): CanonicalLineageArtifactVersionRoot | null {
  if (!value) return null;
  const typed = parseCanonicalLineageRefKey(value);
  return typed?.entity_kind === "artifact_version" ? { entity_kind: "artifact_version", id: typed.id } : null;
}

function lifecycleStates(filters: DagFilterState): string[] {
  const active = filters.pathStates.includes("active");
  const invalidated = filters.pathStates.includes("invalidated");
  if (active && invalidated) return [];
  const states = new Set<string>();
  if (active) states.add("active");
  if (invalidated) ["invalidated", "stale", "superseded"].forEach((state) => states.add(state));
  // An empty lifecycle selection intentionally matches no ArtifactVersion.
  // The contract permits open lifecycle values, so this sentinel preserves
  // global semantics instead of interpreting [] as "all states".
  if (!states.size) states.add("__thread_no_selected_lifecycle_state__");
  return [...states].sort();
}

function queryFilters(filters: DagFilterState, asOf: string | null, now: Date): CanonicalLineageQueryFilters {
  const modelClassifications = ALL_MODEL_CLASSIFICATIONS.filter((value) => (
    filters.modelClassifications.includes(value)
  ));
  if (!modelClassifications.length) {
    throw new Error("At least one model classification must remain selected.");
  }
  const result: CanonicalLineageQueryFilters = {
    // The current display facets are record classes, not canonical Artifact
    // kinds. Sending them as artifact kinds would silently change semantics.
    artifact_kinds: [],
    lifecycle_states: lifecycleStates(filters),
    model_classifications: modelClassifications.length === ALL_MODEL_CLASSIFICATIONS.length
      ? []
      : modelClassifications,
  };
  if (filters.timeWindowDays != null) {
    const end = asOf ? new Date(asOf) : now;
    result.created_before = end.toISOString();
    result.created_after = new Date(end.getTime() - filters.timeWindowDays * 86_400_000).toISOString();
  }
  return result;
}

function timestampFilter(value: string, field: "after" | "before"): string {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    throw new UnsupportedCanonicalLineageSearchError(`${field}:${value} is not a valid date or timestamp`);
  }
  return new Date(parsed).toISOString();
}

function intersectTimestamp(
  current: string | undefined,
  candidate: string,
  field: "after" | "before",
): string {
  if (!current) return candidate;
  const currentTime = Date.parse(current);
  const candidateTime = Date.parse(candidate);
  if (field === "after") return candidateTime > currentTime ? candidate : current;
  return candidateTime < currentTime ? candidate : current;
}

function compileLiveSearch(query: string, baseFilters: CanonicalLineageQueryFilters): {
  query?: string;
  filters: CanonicalLineageQueryFilters;
} {
  const tokens = parseDagSearch(query);
  const filters: CanonicalLineageQueryFilters = {
    ...baseFilters,
    artifact_kinds: [...(baseFilters.artifact_kinds ?? [])],
    lifecycle_states: [...(baseFilters.lifecycle_states ?? [])],
    model_classifications: [...(baseFilters.model_classifications ?? [])],
  };
  const textTerms: string[] = [];
  const requestedLifecycleStates = new Set<string>();
  for (const token of tokens) {
    if (token.sourceField && token.field === "any") {
      throw new UnsupportedCanonicalLineageSearchError(
        `${token.sourceField}: search is not globally supported yet. Use name, kind, state, after, or before.`,
      );
    }
    if (token.negated) {
      throw new UnsupportedCanonicalLineageSearchError("Negative lineage search is not available yet; remove the leading ‘-’.");
    }
    switch (token.field) {
      case "any":
      case "label":
        textTerms.push(token.value);
        break;
      case "kind":
        filters.artifact_kinds!.push(token.value);
        break;
      case "state":
        requestedLifecycleStates.add(token.value);
        break;
      case "after":
        filters.created_after = intersectTimestamp(
          filters.created_after,
          timestampFilter(token.value, "after"),
          "after",
        );
        break;
      case "before":
        filters.created_before = intersectTimestamp(
          filters.created_before,
          timestampFilter(token.value, "before"),
          "before",
        );
        break;
      default:
        throw new UnsupportedCanonicalLineageSearchError(
          `${token.field}: search is not globally supported yet. Use name, kind, state, after, or before.`,
        );
    }
  }
  if (textTerms.length > 1) {
    throw new UnsupportedCanonicalLineageSearchError("Use one free-text or name term; kind, state, after, and before can be combined with it.");
  }
  if (
    filters.created_after
    && filters.created_before
    && Date.parse(filters.created_after) > Date.parse(filters.created_before)
  ) {
    throw new UnsupportedCanonicalLineageSearchError(
      `Lineage search time range is empty: after ${filters.created_after} is later than before ${filters.created_before}.`,
    );
  }
  if (requestedLifecycleStates.size) {
    const baseLifecycleStates = new Set(baseFilters.lifecycle_states ?? []);
    filters.lifecycle_states = baseLifecycleStates.size === 0
      ? [...requestedLifecycleStates]
      : [...requestedLifecycleStates].filter((state) => baseLifecycleStates.has(state));
    if (!filters.lifecycle_states.length) filters.lifecycle_states = ["__thread_no_selected_lifecycle_state__"];
  }
  filters.artifact_kinds = [...new Set(filters.artifact_kinds ?? [])].sort();
  filters.lifecycle_states = [...new Set(filters.lifecycle_states ?? [])].sort();
  return { query: textTerms[0], filters };
}

function hasServerFilters(filters: CanonicalLineageQueryFilters): boolean {
  return (filters.artifact_kinds?.length ?? 0) > 0
    || (filters.lifecycle_states?.length ?? 0) > 0
    || (filters.model_classifications?.length ?? 0) > 0
    || Boolean(filters.name_contains || filters.created_after || filters.created_before);
}

function supportedSummaryGroup(group: DagSummaryGroup): "artifact_kind" | "lifecycle_state" | null {
  if (group === "state") return "lifecycle_state";
  if (group === "category" || group === "kind" || group === "type") return "artifact_kind";
  return null;
}

function isRootedMode(mode: DagViewMode): mode is "focus" | "impact" | "compare" | "time_compare" {
  return mode === "focus" || mode === "impact" || mode === "compare" || mode === "time_compare";
}

/** Convert durable workspace state to one bounded canonical server projection. */
export function buildCanonicalLineageQueryRequest(
  descriptor: CanonicalLineageWorkspaceDescriptor,
  now = new Date(),
): CanonicalLineageQueryRequest {
  const hasRecordedCut = descriptor.recordedAsOf !== null;
  const hasSnapshotToken = descriptor.snapshotToken !== null;
  if (hasRecordedCut !== hasSnapshotToken || (hasRecordedCut && descriptor.asOf === null)) {
    throw new Error("A frozen lineage replay requires effective time, recorded time, and snapshot token together.");
  }
  const filters = queryFilters(descriptor.filters, descriptor.asOf, now);
  const search = compileLiveSearch(descriptor.searchQuery.trim(), filters);
  const base: CanonicalLineageQueryRequest = {
    project_id: descriptor.projectId,
    projection: "snapshot",
    root_refs: [],
    direction: descriptor.direction,
    max_depth: descriptor.depth ?? 32,
    max_records: 500,
    max_paths: 1000,
    page_size: 50,
    as_of: descriptor.asOf,
    ...(descriptor.recordedAsOf && descriptor.snapshotToken ? {
      recorded_as_of: descriptor.recordedAsOf,
      snapshot_token: descriptor.snapshotToken,
    } : {}),
    filters: search.filters,
  };

  if (descriptor.mode === "summary") {
    const groupBy = supportedSummaryGroup(descriptor.summaryGroup);
    if (groupBy) {
      return {
        ...base,
        projection: descriptor.summaryGroup === "category" ? "hierarchy" : "summary",
        group_by: groupBy,
        query: search.query,
      };
    }
    throw new UnsupportedCanonicalLineageSearchError(
      "The canonical summary currently supports Category or State grouping.",
    );
  }

  if (isRootedMode(descriptor.mode) && (search.query || hasServerFilters(search.filters))) {
    throw new UnsupportedCanonicalLineageSearchError(
      `${descriptor.mode} cannot be combined with global search or discovery filters. Return to Chronicle to search, then choose a version anchor.`,
    );
  }

  if (search.query || hasServerFilters(search.filters)) {
    return { ...base, projection: "search", query: search.query };
  }

  const selectedRoot = artifactVersionRoot(descriptor.selectedId);
  if (descriptor.mode === "focus") {
    if (descriptor.selectedId && !selectedRoot) throw new UnsupportedCanonicalLineageRootError("focus");
    if (selectedRoot) return { ...base, projection: "focus", root_refs: [selectedRoot] };
  }
  if (descriptor.mode === "impact") {
    if (descriptor.selectedId && !selectedRoot) throw new UnsupportedCanonicalLineageRootError("impact");
    if (selectedRoot) return { ...base, projection: "trace", root_refs: [selectedRoot], direction: "downstream" };
  }
  if (descriptor.mode === "time_compare") {
    if (descriptor.selectedId && !selectedRoot) throw new UnsupportedCanonicalLineageRootError("time_compare");
    if (selectedRoot) return { ...base, projection: "focus", root_refs: [selectedRoot] };
  }
  if (descriptor.mode === "compare") {
    const rootA = artifactVersionRoot(descriptor.compareA);
    const rootB = artifactVersionRoot(descriptor.compareB);
    if ((descriptor.compareA && !rootA) || (descriptor.compareB && !rootB)) {
      throw new UnsupportedCanonicalLineageRootError("compare");
    }
    const roots = [rootA, rootB]
      .filter((root): root is CanonicalLineageArtifactVersionRoot => root !== null);
    if (roots.length === 2 && roots[0]!.id !== roots[1]!.id) {
      return { ...base, projection: "trace", root_refs: roots, direction: "upstream" };
    }
  }
  // Chronicle, Flow, Runs, Audit, incomplete root-dependent views, and the
  // actor summary fallback all use one deterministic bounded snapshot.
  return base;
}

export function useCanonicalLineageProjection(
  descriptor: CanonicalLineageWorkspaceDescriptor,
  enabled: boolean,
): CanonicalLineageProjectionState {
  const requestResult = useMemo(() => {
    try {
      return { request: buildCanonicalLineageQueryRequest(descriptor), error: null };
    } catch (error) {
      return {
        request: buildCanonicalLineageQueryRequest({
          ...descriptor,
          mode: "chronicle",
          summaryGroup: "category",
          searchQuery: "",
          filters: {
            ...descriptor.filters,
            modelClassifications: [...ALL_MODEL_CLASSIFICATIONS],
          },
          asOf: null,
          recordedAsOf: null,
          snapshotToken: null,
        }),
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  },
    [
      descriptor.asOf,
      descriptor.recordedAsOf,
      descriptor.snapshotToken,
      descriptor.compareA,
      descriptor.compareB,
      descriptor.depth,
      descriptor.direction,
      descriptor.filters,
      descriptor.mode,
      descriptor.projectId,
      descriptor.searchQuery,
      descriptor.selectedId,
      descriptor.summaryGroup,
    ],
  );
  const request = requestResult.request;
  const query = useInfiniteQuery({
    queryKey: ["canonical-lineage-projection", request],
    initialPageParam: null as CanonicalLineagePageReplay | null,
    queryFn: ({ pageParam, signal }) => fetchCanonicalLineageProjection({
      ...request,
      ...(pageParam ?? {}),
    }, { signal }),
    getNextPageParam: (lastPage): CanonicalLineagePageReplay | undefined => lastPage.data.next_cursor ? ({
      cursor: lastPage.data.next_cursor,
      as_of: lastPage.data.effective_as_of,
      recorded_as_of: lastPage.data.recorded_as_of,
      snapshot_token: lastPage.data.snapshot_token,
    }) : undefined,
    enabled: enabled && requestResult.error === null,
    // History navigation must authenticate every A/B replay at its exact
    // request boundary. Cached immutable pages may seed React Query, but they
    // are immediately refetched and DagViewer keeps them hidden until that
    // rebound settles. An unbound server-now freeze is likewise never fresh.
    staleTime: 0,
    retry: false,
  });
  const mergedResult = useMemo(() => {
    try {
      return { value: mergeCanonicalLineagePages(query.data?.pages ?? []), error: null };
    } catch (error) {
      return { value: null, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }, [query.data?.pages]);
  // Never retain a graph across a request/cut transition. React Query aborts
  // the stale transport; this explicit gate prevents a historical/current
  // flash while the new exact cut is pending or invalid.
  const unboundFreezeIsUnsettled = !request.snapshot_token && (query.isFetching || query.error != null);
  const visibleProjection = enabled
    && requestResult.error === null
    && !unboundFreezeIsUnsettled
    ? mergedResult.value
    : null;
  const graph = useMemo(
    () => visibleProjection ? normalizeDagData(canonicalProjectionToDag(visibleProjection)) : null,
    [visibleProjection],
  );
  const retainedPageCount = query.data?.pages.length ?? 0;
  const maximumNextPageRecords = request.max_records ?? 500;
  const serverHasNextPage = requestResult.error === null && Boolean(query.hasNextPage);
  const retentionLimitReached = serverHasNextPage && (
    retainedPageCount >= CANONICAL_LINEAGE_MAX_RETAINED_PAGES
    || (visibleProjection?.loadedRecordCount ?? 0) + maximumNextPageRecords > CANONICAL_LINEAGE_MAX_RETAINED_RECORDS
  );

  return {
    graph,
    projection: visibleProjection,
    isPending: enabled && requestResult.error === null && query.status === "pending",
    isFetching: enabled && requestResult.error === null && query.isFetching,
    isFetchingNextPage: enabled && requestResult.error === null && query.isFetchingNextPage,
    hasNextPage: serverHasNextPage && !retentionLimitReached,
    retentionLimitReached,
    error: requestResult.error ?? mergedResult.error ?? (query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null),
    fetchNextPage: async () => retentionLimitReached || requestResult.error ? undefined : query.fetchNextPage(),
  };
}
